"use client";

import { Button } from "@heroui/button";
import { Accordion, AccordionItem } from "@heroui/accordion";
import { useEffect, useState } from "react";
import {
  MdOutlineDangerous,
  MdCode,
  MdRefresh,
  MdBugReport,
  MdCheckCircle,
} from "react-icons/md";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    // Log de l'erreur pour le débogage
    console.error("Critical Error:", error);
  }, [error]);

  const handleReport = async () => {
    setReporting(true);
    try {
      await fetch("/api/auth/email/errorreport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: error.name,
          message: error.message,
          digest: error.digest,
          stack: error.stack,
        }),
      });
      setReported(true);
    } catch (err) {
      console.error("Failed to report error", err);
    } finally {
      setReporting(false);
    }
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-[70vh] px-6 gap-6">
      {/* --- ICON ET TITRE --- */}
      <div className="flex flex-col items-center gap-2">
        <div className="p-4 bg-danger/10 rounded-full text-danger animate-pulse">
          <MdOutlineDangerous size={50} />
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white text-center tracking-tight">
          Oups ! Une erreur s'est produite
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-center max-w-md font-medium">
          L'application a rencontré un problème inattendu. Nos équipes ont été
          notifiées.
        </p>
      </div>

      {/* --- ACTION --- */}
      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center mt-2">
        <Button
          size="lg"
          radius="full"
          className="bg-[#E31D2B] text-white font-bold shadow-lg shadow-red-500/30 px-8"
          startContent={<MdRefresh size={20} />}
          onPress={() => reset()}
        >
          Réessayer
        </Button>
        <Button
          size="lg"
          radius="full"
          variant="flat"
          color={reported ? "success" : "default"}
          className="font-bold px-8"
          startContent={
            reported ? <MdCheckCircle size={20} /> : <MdBugReport size={20} />
          }
          isLoading={reporting}
          isDisabled={reported}
          onPress={handleReport}
        >
          {reported ? "Signalée" : "Signaler au développeur"}
        </Button>
      </div>

      {/* --- DETAILS TECHNIQUES (ACCORDION) --- */}
      <div className="w-full max-w-2xl mt-4">
        <Accordion variant="splitted" selectionMode="multiple">
          <AccordionItem
            key="1"
            aria-label="Détails techniques"
            title="Détails techniques de l'erreur"
            startContent={<MdCode className="text-slate-400" size={20} />}
            classNames={{
              base: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm",
              title: "text-sm font-bold text-slate-600 dark:text-slate-300",
              content: "pb-4",
            }}
          >
            <div className="flex flex-col gap-3">
              <div className="p-4 bg-slate-50 dark:bg-black/40 rounded-xl border border-slate-100 dark:border-slate-800 font-mono text-xs overflow-x-auto">
                <p className="text-[#E31D2B] font-bold mb-1 underline">
                  Message :
                </p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                  {error.message || "Aucune description fournie."}
                </p>

                {error.digest && (
                  <>
                    <p className="text-slate-500 font-bold mb-1">
                      ID d'erreur (Digest) :
                    </p>
                    <p className="text-slate-400 mb-4">{error.digest}</p>
                  </>
                )}

                <p className="text-slate-500 font-bold mb-1">Stack trace :</p>
                <p className="text-slate-400 whitespace-pre-wrap opacity-60">
                  {error.stack ||
                    "La trace n'est pas disponible en production."}
                </p>
              </div>
              <p className="text-[10px] text-slate-400 text-center italic">
                Ces informations aident nos développeurs à corriger le problème.
              </p>
            </div>
          </AccordionItem>
        </Accordion>
      </div>

      {/* --- LIEN RETOUR --- */}
      <Button
        as="a"
        href="/"
        variant="light"
        className="text-slate-500 font-semibold"
      >
        Retourner à l'accueil
      </Button>
    </div>
  );
}
